/**
 * useTutorial — contextual onboarding for first-time players.
 *
 * Steps appear as floating tooltips triggered by game state:
 *   1. "Click the start word to explore" — shown when game starts with 1 node
 *   2. (dismissed on first expand)
 *   3. "Drag one word onto another to combine" — shown after 3rd expand
 *   4. (dismissed on first combine)
 *
 * Persists "has completed tutorial" in localStorage so it only shows once.
 */

import { useState, useCallback, useEffect, useRef } from 'react';

const STORAGE_KEY = 'lexiquest-tutorial-done';

function hasDoneTutorial() {
  try { return localStorage.getItem(STORAGE_KEY) === '1'; } catch { return false; }
}

// Tutorial steps
const STEPS = {
  CLICK_START: 'click_start',
  COMBINE_HINT: 'combine_hint',
  DONE: 'done',
};

export default function useTutorial() {
  const [active, setActive] = useState(!hasDoneTutorial());
  const [step, setStep] = useState(STEPS.CLICK_START);
  const expandCount = useRef(0);
  const hasCombined = useRef(false);

  // Skip tutorial if already done
  useEffect(() => {
    if (hasDoneTutorial()) {
      setActive(false);
      setStep(STEPS.DONE);
    }
  }, []);

  const onExpand = useCallback(() => {
    if (!active) return;
    expandCount.current += 1;

    if (step === STEPS.CLICK_START) {
      // After first expand, dismiss the click hint
      // Show combine hint after 3 expansions
      if (expandCount.current >= 3 && !hasCombined.current) {
        setStep(STEPS.COMBINE_HINT);
      } else {
        setStep(STEPS.DONE); // temporarily hide, will show combine hint later
      }
    } else if (step === STEPS.DONE && expandCount.current >= 3 && !hasCombined.current) {
      setStep(STEPS.COMBINE_HINT);
    }
  }, [active, step]);

  const onCombine = useCallback(() => {
    if (!active) return;
    hasCombined.current = true;
    setStep(STEPS.DONE);
    setActive(false);
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch {}
  }, [active]);

  const dismiss = useCallback(() => {
    setStep(STEPS.DONE);
    setActive(false);
    try { localStorage.setItem(STORAGE_KEY, '1'); } catch {}
  }, []);

  const reset = useCallback(() => {
    expandCount.current = 0;
    hasCombined.current = false;
    if (!hasDoneTutorial()) {
      setActive(true);
      setStep(STEPS.CLICK_START);
    }
  }, []);

  return {
    active,
    step,
    STEPS,
    onExpand,
    onCombine,
    dismiss,
    reset,
    showClickHint: active && step === STEPS.CLICK_START,
    showCombineHint: active && step === STEPS.COMBINE_HINT,
  };
}
